import {ErrorMessagesForNewCreditCardInput, ErrorMessagesForPaymentMethodInput, NewCreditCardInputInfo} from '../../../../Props/ContractInputInfo'
import React from 'react'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {ErrorDisplay} from '../../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MBMemberInput} from '../../../Atoms/MBMemberInput'
import {FlexContainer} from '../../../Atoms/Containers'
import {MBMemberSelect} from '../../../Atoms/MBMemberSelect'
import {CALENDAR} from '../../../../../Common/Constants'
import {MBContentNoteWithMarkTypography} from '../../../Atoms/Typographies'
import card from '../../../../../img/card.png'
import {convertOnBlur} from '../../../../Utils/ConvertOnBlur'

type NewCreditCardInputComponentForProps = {
  newCreditCardInputInfo: NewCreditCardInputInfo
  errorMessages: ErrorMessagesForNewCreditCardInput
  onChangeInputDetailInfo: (changeInputDetailInfo: NewCreditCardInputInfo) => void
}

export const NewCreditCardInputComponent =
  (props: NewCreditCardInputComponentForProps): JSX.Element => {
    const creditCardInfoState = props.newCreditCardInputInfo
      || {
        cardNumber: '',
        expireMonth: '',
        expireYear: '',
        holderName: '',
        securityCode: '',
      } as NewCreditCardInputInfo

    function updateCreditCardInfo(
      name: string, value: string, creditCardInfoState: NewCreditCardInputInfo,
    ): NewCreditCardInputInfo {
      return {
        ...creditCardInfoState,
        [name]: value,
      }
    }

    function handleChangeInput(
      e: React.ChangeEvent<HTMLInputElement>, creditCardInfoState: NewCreditCardInputInfo,
    ): NewCreditCardInputInfo {
      const {name, value} = e.target
      return updateCreditCardInfo(name, value, creditCardInfoState)
    }

    function handleChangeSelect(
      e: React.ChangeEvent<HTMLSelectElement>, creditCardInfoState: NewCreditCardInputInfo,
    ): NewCreditCardInputInfo {
      const {name, value} = e.target
      return updateCreditCardInfo(name, value, creditCardInfoState)
    }

    const hasErrorForInvalidCard = props.errorMessages.invalidCardErrors.length > 0

    return (
      <Spacer ml={'10%'} dataTestId="block-input-credit-card-information">
        <Spacer mt={'6px'} mb={'12px'}>
          <ErrorDisplay errors={props.errorMessages.invalidCardErrors}/>
        </Spacer>
        <Spacer mt={'6px'} mb={'12px'}>
          <p className="text-hdg_01">
            以下のカード情報は全てカードの表記通り半角英数字で正確に入力してください。
            <span id="card_text"/>
          </p>
        </Spacer>
        <Spacer my={'4px'}><label htmlFor="cardNumber">カード番号</label></Spacer>
        <Spacer mt={'4px'} mb={'12px'}>
          <MBMemberInput
            id="cardNumber"
            name="cardNumber"
            placeholder="例）1234567890123456"
            type="tel"
            className="form-text_01 w_100"
            minLength={10}
            maxLength={16}
            value={creditCardInfoState.cardNumber}
            hasError={
              props.errorMessages.creditCardNumberErrors.length > 0 ||
              hasErrorForInvalidCard
            }
            onChange={(e): void => {
              props.onChangeInputDetailInfo(
                handleChangeInput(e, creditCardInfoState),
              )
            }}
            onBlur={(e): void => {
              props.onChangeInputDetailInfo(
                updateCreditCardInfo(
                  e.target.name,
                  convertOnBlur(e.target.name, e.target.value),
                  creditCardInfoState,
                ),
              )
            }}
          />
          <Spacer mt={'10px'}>
            <ErrorDisplay errors={props.errorMessages.creditCardNumberErrors}/>
          </Spacer>
        </Spacer>
        <Spacer my={'4px'}><label htmlFor="cardExpirationMonth">カード有効期限</label></Spacer>
        <Spacer mt={'4px'} mb={'12px'}>
          <FlexContainer>
            <MBMemberSelect
              id="cardExpirationMonth"
              name="expireMonth"
              width={'35%'}
              value={creditCardInfoState.expireMonth}
              hasError={
                props.errorMessages.creditCardExpireMonthErrors.length > 0 ||
                props.errorMessages.creditCardFutureExpireDateErrors.length > 0 ||
                hasErrorForInvalidCard
              }
              onChange={(e): void => {
                props.onChangeInputDetailInfo(
                  handleChangeSelect(e, creditCardInfoState),
                )
              }}
            >
              <option value="" hidden disabled>月</option>
              {CALENDAR.MONTHS.map((month, i) => (
                <option key={i} value={month || ''}>{month}</option>
              ))}
            </MBMemberSelect>
            <Spacer mx={'5px'} my={'7px'}>／</Spacer>
            <MBMemberSelect
              id="cardExpirationYear"
              name="expireYear"
              className="form-select_01 w_45"
              value={creditCardInfoState.expireYear}
              hasError={
                props.errorMessages.creditCardExpireYearErrors.length > 0 ||
                props.errorMessages.creditCardFutureExpireDateErrors.length > 0 ||
                hasErrorForInvalidCard
              }
              onChange={(e): void => {
                props.onChangeInputDetailInfo(
                  handleChangeSelect(e, creditCardInfoState),
                )
              }}
            >
              <option value="" hidden disabled>年</option>
              {CALENDAR.YEARS_FOR_CREDIT_CARD.map((year, i) => (
                <option key={i} value={year || ''}>{year}</option>
              ))}
            </MBMemberSelect>
          </FlexContainer>
          <Spacer mt={'10px'}>
            <ErrorDisplay errors={[
              ...props.errorMessages.creditCardExpireMonthErrors,
              ...props.errorMessages.creditCardExpireYearErrors,
              ...props.errorMessages.creditCardFutureExpireDateErrors,
            ]}/>
          </Spacer>
        </Spacer>
        <Spacer my={'4px'}><label htmlFor="CDTCRD_HOLDER">名義人</label></Spacer>
        <Spacer mt={'4px'} mb={'12px'}>
          <MBMemberInput
            id="CDTCRD_HOLDER"
            name="holderName"
            type="text"
            className="form-text_01 w_100"
            maxLength={50}
            placeholder="例）TARO YAMADA"
            value={creditCardInfoState.holderName}
            hasError={
              props.errorMessages.creditCardHolderNameErrors.length > 0 ||
              hasErrorForInvalidCard
            }
            onChange={(e): void => {
              props.onChangeInputDetailInfo(
                handleChangeInput(e, creditCardInfoState),
              )
            }}
          />
          <Spacer mt={'10px'}>
            <ErrorDisplay errors={props.errorMessages.creditCardHolderNameErrors}/>
          </Spacer>
        </Spacer>
        <SecurityCodeInputForm
          securityCode={creditCardInfoState.securityCode}
          errorMessages={{
            ...props.errorMessages,
            myCreditCardSecurityCodeErrors: [],
          }}
          onChangeInputSecurityCode={(value): void => {
            props.onChangeInputDetailInfo(
              updateCreditCardInfo('securityCode', value, creditCardInfoState),
            )
          }}
        />
      </Spacer>
    )
  }

type ErrorMessageListForSecurityCodeInputForm =
  Pick<ErrorMessagesForPaymentMethodInput,
    'invalidCardErrors' |
    'myCreditCardSecurityCodeErrors' |
    'creditCardSecurityCodeErrors'>

type SecurityCodeInputFormProps = {
  securityCode: string
  errorMessages: ErrorMessageListForSecurityCodeInputForm
  onChangeInputSecurityCode: (changeInputSecurityCode: string) => void
}

export const SecurityCodeInputForm = (props: SecurityCodeInputFormProps): JSX.Element => {
  return (
    <>
      <Spacer my={'4px'}><label htmlFor="security-code">セキュリティコード</label></Spacer>
      <Spacer mt={'4px'} mb={'12px'}>
        <MBMemberInput
          id="security-code"
          name="securityCode"
          type="tel"
          autoComplete={'off'}
          className="form-text_01 w_70px"
          minLength={3}
          maxLength={4}
          value={props.securityCode}
          hasError={
            props.errorMessages.invalidCardErrors.length > 0 ||
            props.errorMessages.myCreditCardSecurityCodeErrors.length > 0 ||
            props.errorMessages.creditCardSecurityCodeErrors.length > 0
          }
          onChange={(e): void => {
            props.onChangeInputSecurityCode(e.target.value)
          }}
          onBlur={(e): void => {
            props.onChangeInputSecurityCode(convertOnBlur(e.target.name, e.target.value))
          }}
        />
        <Spacer mb={'12px'}>
          <ErrorDisplay errors={[
            ...props.errorMessages.creditCardSecurityCodeErrors,
            ...props.errorMessages.myCreditCardSecurityCodeErrors,
          ]}/>
        </Spacer>
        <Spacer mb={'20px'}>
          <Spacer mb={'4px'}>
            <MBContentNoteWithMarkTypography
              note={'セキュリティコードとはクレジットカード番号とは別にカード裏面に印刷されている3桁の数字です。'}
            />
          </Spacer>
          <MBContentNoteWithMarkTypography
            note={'AMERICAN EXPRESS カードは表面右上または左上に4桁の数字で印刷されています。'}
          />
        </Spacer>
        <div><img className="mw_100" src={card} alt="example-security-code"/></div>

      </Spacer>
    </>
  )
}
